import { LinkedProjectConfig } from '../types';
import { LinkedBillingMode, TAGESPAUSCHALE } from './linkedBillingMode';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE MUTTER-TOCHTER-VERKNUEPFUNG — aus den Spalten, nicht aus dem Blob
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis Nr. 71/72 lebte die Verknuepfung allein in
 * `data->metadata->globalData->linkedProjectConfig`, also in dem jsonb, das
 * bei jedem Speichern komplett ersetzt wird. Am 02.09.2026 hat eine Sitzung
 * mit einem Stand von VOR dem Verknuepfen gespeichert — die Bergung rettet
 * nur das Protokoll — und die Tochter 6290-2 stand ohne Verknuepfung und
 * damit ohne LV da (Gedaechtnisnotiz `verknuepfung-im-blob-ungeschuetzt`).
 *
 * Seit Nr. 71 sind `projects.parent_project_id`, `linked_billing_mode` und
 * `linked_billing_history` die Wahrheit. Diese Datei setzt daraus das
 * `LinkedProjectConfig` zusammen, mit dem der gesamte Client weiterarbeitet —
 * KEIN Lesepfad ausserhalb der Ladefunktionen muss umgebaut werden.
 *
 * ─── Die Regel: die Spalte gewinnt IMMER ────────────────────────────────────
 * Steht die Spalte auf NULL, ist das Projekt keine Tochter — auch wenn im Blob
 * noch ein `linkedProjectConfig` liegt. Sonst holte ein alter Client, der das
 * Blob-Feld noch schreibt, eine getrennte Verknuepfung zurueck ins Leben.
 *
 * Einzige Ausnahme: die Spalte kommt gar nicht mit (`undefined`, nicht `null`).
 * Das heisst „Nr. 71 ist in dieser Datenbank nicht gelaufen" (TEST, oder der
 * Spalten-Rueckfall hat sie weggelassen). Dann gibt es keine Wahrheit, die
 * gewinnen koennte, und das Blob-Feld ist das einzige, was da ist.
 *
 * ─── Der Name der Mutter ───────────────────────────────────────────────────
 * Es gibt keine Spalte dafuer, und es soll keine geben: der Name ist Anzeige
 * und aenderbar. Die Ladepfade schlagen ihn nach (`projects.name` der Mutter);
 * bis dahin dient der Name aus dem Blob-Feld als Vorbelegung — aber nur, wenn
 * das Blob-Feld dieselbe Mutter meint.
 */

/** Die drei Spalten aus Nr. 71, so wie PostgREST sie liefert. */
export interface ProjectLinkColumns {
    parent_project_id?: string | null;
    linked_billing_mode?: string | null;
    linked_billing_history?: unknown;
}

export const PROJECT_LINK_COLUMNS = ['parent_project_id', 'linked_billing_mode', 'linked_billing_history'] as const;

const istUuid = (s: unknown): s is string =>
    typeof s === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(s);

const alsModus = (m: unknown): LinkedBillingMode =>
    m === 'individual' ? 'individual' : TAGESPAUSCHALE;

const alsHistorie = (h: unknown): LinkedProjectConfig['billingModeHistory'] => {
    if (!Array.isArray(h)) return undefined;
    const sauber = h
        .filter((e): e is { mode: unknown; from: unknown } => !!e && typeof e === 'object')
        .filter((e) => (e.mode === 'daily_flat' || e.mode === 'individual') && typeof e.from === 'string')
        .map((e) => ({ mode: e.mode as LinkedBillingMode, from: e.from as string }));
    return sauber.length > 0 ? sauber : undefined;
};

/**
 * Setzt aus den Spalten das `LinkedProjectConfig` zusammen.
 *
 *   Spalte gesetzt   → Tochter; Blob nur fuer den Namen, und nur bei gleicher Mutter.
 *   Spalte NULL      → keine Tochter, Blob-Feld wird IGNORIERT.
 *   Spalte fehlt     → Nr. 71 nicht gelaufen; Blob-Feld ist der Rueckfall.
 *
 * `parentName` ueberstimmt jeden Namen aus dem Blob — er kommt frisch aus
 * `projects.name` der Mutter.
 */
export const linkConfigFromRow = (
    row: ProjectLinkColumns | null | undefined,
    blob: LinkedProjectConfig | null | undefined,
    parentName?: string | null,
): LinkedProjectConfig | undefined => {
    const spalte = row?.parent_project_id;

    if (spalte === undefined) {
        // Nr. 71 fehlt in dieser Datenbank — es gibt nur das Blob-Feld.
        if (blob && blob.role === 'child' && blob.linkedProjectId) return { ...blob };
        return undefined;
    }

    if (spalte === null || !istUuid(spalte)) return undefined;

    const blobPasst = !!blob && blob.role === 'child' && blob.linkedProjectId === spalte;
    const name = (parentName && parentName.trim())
        || (blobPasst ? (blob!.linkedProjectName || '') : '');

    const config: LinkedProjectConfig = {
        role: 'child',
        linkedProjectId: spalte,
        linkedProjectName: name,
        billingMode: alsModus(row?.linked_billing_mode),
    };
    const historie = alsHistorie(row?.linked_billing_history);
    if (historie) config.billingModeHistory = historie;
    return config;
};

/**
 * Traegt das zusammengesetzte Config in `data.metadata.globalData` ein — oder
 * entfernt das Blob-Feld, wenn die Spalte sagt: keine Tochter.
 *
 * Arbeitet IN PLACE auf dem geladenen Projektobjekt, wie die uebrigen
 * Angleichungen in den Ladepfaden (`nextScheduledDate`, `category`, …).
 * Liefert die Mutter-Kennung zurueck, damit der Aufrufer den Namen nachschlagen
 * kann, ohne das Config ein zweites Mal zu lesen.
 */
export const applyLinkColumns = (
    project: { data?: any } & ProjectLinkColumns,
    parentName?: string | null,
): string | null => {
    if (!project?.data) return null;
    if (!project.data.metadata) project.data.metadata = {};
    if (!project.data.metadata.globalData) project.data.metadata.globalData = {};
    const globalData = project.data.metadata.globalData;
    const config = linkConfigFromRow(project, globalData.linkedProjectConfig, parentName);
    if (config) {
        globalData.linkedProjectConfig = config;
        return config.linkedProjectId;
    }
    delete globalData.linkedProjectConfig;
    return null;
};

/** Traegt den nachgeschlagenen Namen der Mutter nach — nur, wenn das Config steht. */
export const setLinkedParentName = (project: { data?: any }, name: string | null | undefined): void => {
    const config = project?.data?.metadata?.globalData?.linkedProjectConfig as LinkedProjectConfig | undefined;
    if (!config || !name || !name.trim()) return;
    config.linkedProjectName = name.trim();
};
